import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import FeedThumb from '../../../../UserWidgets/_partials/FeedThumb';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import MeasuredViewerContainerSizeContextProvider from '@wix/da-shared-react/pkg/DaEditorWrapper/ViewerContainerSizeContext/MeasuredViewerContainerSizeContextProvider';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured/redux/types';
import LoadableDuperbrowseContext from '../../../../../contexts/LoadableDuperbrowseContext';
import FeedPagination from './FeedPagination';

import s from './PostsFeed.scss';

export interface Props {
  streamId: string;
  className?: string;
  isOwner: boolean;

  items: PapiDeviation[];

  isItemFeatured: (item: PapiDeviation) => boolean;
  editItem: (item: PapiDeviation) => void;
  featureItem: (item: PapiDeviation) => void;
  unfeatureItem: (item: PapiDeviation) => void;
  pinItem: (item: PapiDeviation) => void;
  unpinItem: () => void;
}

const PostsFeed: React.FC<Props> = ({
  isOwner,
  items,
  streamId,
  className,
  pinItem,
  unpinItem,
  featureItem,
  unfeatureItem,
  editItem,
  isItemFeatured,
}) => {
  const { t } = useTranslation();
  return (
    <ErrorBoundary>
      <LoadableDuperbrowseContext
        streamId={streamId}
        parent={{ title: t('duperbrowse.backButton.profile.posts') }}
      >
        <MeasuredViewerContainerSizeContextProvider
          cookieType={MeasuredCookieType.BROWSE_POST_FEED}
          cookieDim={'width'}
          className={classnames(s['items'], className)}
          innerPadding={16}
          listenOnWindow
        >
          {items.map((item, index) => {
            const isFeatured = isItemFeatured(item);
            const biData = {
              evid: BiEventType.WATCH_MENU_ITEM_SELECTION,
              typeid: getDeviationResourceType(item),
              itemid: item.deviationId,
              rank: index,
            };
            return (
              <FeedThumb
                key={`${index}-${item.typeId}-${item.deviationId}-${item['isPinned']}`}
                withWatch
                withComments
                deviation={item}
                className={s['feed-item']}
                extraActions={
                  !isOwner
                    ? undefined
                    : [
                        !isFeatured && {
                          label: t('widgets.feed.feature'),
                          onClick: () => featureItem(item),
                          biData: { ...biData, menu_item: 'feature' },
                        },
                        isFeatured && {
                          label: t('widgets.feed.unfeature'),
                          onClick: () => unfeatureItem(item),
                          biData: { ...biData, menu_item: 'unfeature' },
                        },
                      ]
                }
              />
            );
          })}
        </MeasuredViewerContainerSizeContextProvider>
        <FeedPagination streamId={streamId} />
      </LoadableDuperbrowseContext>
    </ErrorBoundary>
  );
};

PostsFeed.displayName = 'PostsFeed';
export default PostsFeed;
